import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { useGetSupportUrl } from '@/hooks/useGetSupportUrl';
import { useUnlimitedFirstMonthCreditsEnabled } from '@/hooks/useUnlimitedFirstMonthCreditsEnabled';
import Billing from '@/images/Billing.svg';
import HelpCenter from '@/images/HelpCenter.svg';
import Info from '@/images/Info.svg';
import Login from '@/images/Login.svg';
import Logout from '@/images/Logout.svg';
import SettingsApi from '@/images/SettingsApi.svg';
import Support from '@/images/Support.svg';
import { useAnalytics } from '@/services/analytics';
import { Credit } from '@/services/api';
import { DidUser, useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes, formatCredits, useCreditQuery } from '@/state/credits/credits';
import { useGetOrganization } from '@/state/video-library/videoLibrary';
import { AppRoutes, CssProps } from '@/utils/types';
import { isEnterprisePlan, PlanGroup, productToProductName, ProductType } from '@d-id/types';
import styled from '@emotion/styled';
import { Grid, LinearProgress, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleList from '../List/SimpleList';
import SimpleNotificationModal from '../Modals/SimpleNotificationModal';
import GroupArrow from './images/GroupArrow.svg';

const PlanBadge = styled(Typography)`
    color: ${({ theme }) => theme.colors.primary.main};
    background-color: ${({ theme }) => theme.colors.primary.bg};
    width: fit-content;
    padding: 2px 4px;
    border-radius: 4px;
    text-transform: uppercase;
    margin-bottom: 0.75rem;
`;

const PopupContainer = styled.div<{ isOpen: boolean; left?: string }>`
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'none')};
    pointer-events: ${({ isOpen }) => (isOpen ? 'all' : 'none')};
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    position: absolute;
    background: #fffefd;
    box-shadow: 0px 10px 60px 0px #00000033;
    border-radius: 8px;
    font-size: 1.6rem;
    z-index: 19;
    transition: opacity 0.1s ease-in-out;
    display: flex;
    width: 240px;
    padding: 16px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    left: ${({ left }) => left || '22rem'};
    color: black;
`;
const InnerPricingContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;
const MenuContainer = styled.div`
    width: 100%;
`;

const listSx: CssProps = {
    background: 'white',
    '& .MuiListItemButton-root': {
        padding: '0.5rem 0.2rem',
    },
    '& .MuiListItemText-primary': {
        color: 'black',
        fontSize: '1.4rem',
    },
    '& .MuiListItemIcon-root': {
        minWidth: '3rem',
    },
};

const CreditsContainer = styled.div`
    display: flex;
    flex-direction: initial;
    gap: 8px;
    align-items: center;
`;

const UserInfoText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 1px;
`;

const CreditsUsageIndicator = styled(LinearProgress)`
    height: 0.75rem;
    border-radius: 8px;
    margin-top: 1rem;
    background-color: ${({ theme }) => theme.colors.black[100]};

    & .MuiLinearProgress-bar {
        background-color: #1d1d1d;
    }
`;

const Arrow = styled.img`
    position: absolute;
    left: -9px;
    top: 90%;
    width: 21px;
    height: 21px;
`;

const CreditsSkeleton = styled(Skeleton)<{ width: string }>`
    height: 2.4rem;
    width: ${({ width }) => width};
`;

const UsedCredits = styled.span<{ isExceeding: boolean }>`
    color: ${({ isExceeding, theme }) => (isExceeding ? theme.colors.error.main : theme.colors.black.main)};
`;

const calculateCreditsUsage = (credits: Credit): number => {
    const { remaining, total } = credits;
    const used = total - remaining;
    const percentage = 100 * (used / total);
    return Math.min(percentage, 100);
};

const getPlanText = (isOrg: boolean, isOrgNameFetched: boolean, orgName: string, user: DidUser | null | undefined) => {
    if (isOrg) {
        return isOrgNameFetched ? orgName : 'Loading...';
    }
    const userPlan = user?.planGroup ?? user?.productName;
    return userPlan && `${productToProductName[userPlan]}${[PlanGroup.GUEST, ProductType.GUEST].includes(userPlan) ? '' : ' plan'}`;
};

interface Props {
    isOpen: boolean;
    closePopup?: () => void;
    className?: string;
    onOutsideClick?: () => void;
    left: string;
}

export const ProfilePopup = ({ isOpen, className, onOutsideClick, left, closePopup }: Props) => {
    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { user, isAnonymous, logout, loginWithRedirect } = useAuth();
    const isOrg = !!user?.org_id;
    const { data: org, isFetched: isOrgNameFetched } = useGetOrganization();
    const { data: credits, isFetched } = useCreditQuery();
    const popupRef = useRef<HTMLDivElement>(null);
    const supportUrl = useGetSupportUrl();
    const [openMobileModal, setOpenMobileModal] = React.useState(false);
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const { enabled: unlimitedFirstMonthCreditsEnabled, isFetched: unlimitedFirstMonthCreditsIsFetched } =
        useUnlimitedFirstMonthCreditsEnabled(user);

    const handleClickOutside = (e: any) => {
        if (onOutsideClick && popupRef.current && !popupRef.current.contains(e.target)) {
            onOutsideClick();
        }
    };

    useEffect(() => {
        if (onOutsideClick && isOpen) {
            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 50);
        } else if (onOutsideClick) {
            document.removeEventListener('click', handleClickOutside);
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen]);

    const onLogoutClick = () => {
        mixpanel?.track('logout', { event: 'click' });
        logout({
            returnTo: window.location.origin,
        });
    };

    const onLoginClick = () => {
        mixpanel?.track('login-signup', { event: 'click', isAnonymous, from: 'side-menu' });
        loginWithRedirect({ screen_hint: 'signup' });
    };

    const divider = {
        title: 'divider',
        disabled: true,
        icon: '',
        onClick: () => {},
    };

    const onClick = () => {
        closePopup?.();
        navigate(AppRoutes.AccountSettings, { replace: true });
    };

    const simpleListItems = user
        ? [
              divider,
              {
                  title: 'Account & API',
                  icon: SettingsApi,
                  onClick,
              },
              {
                  title: 'Billing',
                  icon: Billing,
                  onClick,
                  disabled: [PlanGroup.GUEST, PlanGroup.TRIAL, PlanGroup.FREE].includes(user.planGroup as any),
                  hide: isEnterprisePlan(user?.planGroup),
              },
              {
                  title: 'Help center',
                  icon: HelpCenter,
                  onClick: () => {
                      mixpanel?.track('contact', { event: 'click', from: 'help-center' });
                      closePopup?.();
                      window.open('https://help.d-id.com/', '_blank');
                  },
              },
              {
                  title: 'Contact support',
                  icon: Support,
                  onClick: () => {
                      mixpanel?.track('contact', { event: 'click', from: 'support' });
                      closePopup?.();
                      window.open(supportUrl, '_blank');
                  },
                  hide: [PlanGroup.GUEST].includes(user.planGroup as any),
              },
              divider,
              {
                  title: isAnonymous ? 'Login/Signup' : 'Logout',
                  disabled: !user,
                  icon: isAnonymous ? Login : Logout,
                  onClick: isAnonymous ? onLoginClick : onLogoutClick,
              },
          ]
        : [];

    const renderCredits = () => {
        if (isAnonymous) {
            return null;
        }
        if (!isFetched || !unlimitedFirstMonthCreditsIsFetched) {
            return (
                <>
                    <CreditsSkeleton width="50%" />
                    <CreditsSkeleton width="100%" />
                </>
            );
        }

        return (
            <>
                <CreditsContainer>
                    <Grid container justifyContent="space-between">
                        <Grid display="flex" alignItems="center" justifyContent="center" gap={'4px'}>
                            <Typography variant="h4" fontWeight={600}>
                                Credits
                            </Typography>
                            {unlimitedFirstMonthCreditsEnabled && (
                                <CustomTooltip
                                    cursor="pointer"
                                    title={
                                        <div>
                                            Unlimited videos for your first month.
                                            <br />
                                            Fair-use limits apply. See Fair-Use Policy.
                                        </div>
                                    }
                                    display="flex"
                                    placement="top-start">
                                    <img src={Info} alt="info icon" width="16px" height="16px" />
                                </CustomTooltip>
                            )}
                        </Grid>
                        <UserInfoText>
                            <Typography variant="h5" fontWeight={500}>
                                {creditsToMinutesEnabled ? (
                                    <>
                                        <UsedCredits isExceeding={credits!.remaining < 0}>
                                            {creditsToVideoMinutes(credits!.remaining)}
                                        </UsedCredits>
                                        {'/' + creditsToVideoMinutes(credits!.total)}
                                    </>
                                ) : (
                                    <>
                                        <UsedCredits isExceeding={credits!.remaining < 0}>
                                            {unlimitedFirstMonthCreditsEnabled && (credits?.remaining ?? 0) > 0
                                                ? 'Unlimited'
                                                : formatCredits(credits!.remaining)}
                                        </UsedCredits>
                                        {!unlimitedFirstMonthCreditsEnabled && <>{'/' + formatCredits(credits!.total)}</>}
                                    </>
                                )}
                            </Typography>
                        </UserInfoText>
                    </Grid>
                </CreditsContainer>
                {<CreditsUsageIndicator variant="determinate" value={calculateCreditsUsage(credits!)} />}
            </>
        );
    };

    return (
        <>
            <PopupContainer className={className} isOpen={isOpen} ref={popupRef} left={left}>
                <InnerPricingContainer>
                    <PlanBadge variant="h6">{getPlanText(isOrg, isOrgNameFetched, org?.name ?? '', user)}</PlanBadge>
                    {renderCredits()}
                    <MenuContainer>
                        <SimpleList style={listSx} items={simpleListItems} />
                    </MenuContainer>
                </InnerPricingContainer>
                <Arrow src={GroupArrow} />
            </PopupContainer>

            <SimpleNotificationModal
                open={openMobileModal}
                onClose={() => {
                    setOpenMobileModal(false);
                }}
                content="Please use the mobile app to manage your plan."
                primaryActionContent="Got It"
                onPrimaryAction={() => {
                    setOpenMobileModal(false);
                }}
                buttonPlacement="center"
            />
        </>
    );
};
